import React, { useRef, useEffect, useMemo } from "react";
import * as d3 from "d3";
import { useDimensions } from "./useDimensions";
import useIsMobile from "./useIsMobile";

const LocationFrequency = ({ chinaData, usaData, japanData, taiwanData }) => {

    const svgRef = useRef();

    const containerRef = useRef();
    const dimensions = useDimensions(containerRef); 

    const isMobile = useIsMobile();

    const datasets = useMemo(() => [
        { country:"china", cn: "中国", data: chinaData, color: "var(--china)" },
        { country:"taiwan", cn: "台湾", data: taiwanData, color: "var(--taiwan)" },
        { country:"japan", cn: "日本", data: japanData, color: "var(--japan)" },
        { country:"usa", cn: "美国", data: usaData, color: "var(--usa)" }
    ], [chinaData, usaData, japanData, taiwanData]);

    useEffect(() => {
        if (!chinaData || !usaData || !japanData || !taiwanData) return;

        // const totalCount = data.length;
        // const locationCounts = d3.rollup(
        // data,
        // (v) => v.length,
        // (d) => d.scene
        // );

        const allScenes = new Set();
        datasets.forEach(({ data }) => {
            data.forEach(d => allScenes.add(d.scene));
        });


        // const locations = Array.from(locationCounts, ([scene, count]) => ({ scene, count }));
        
        // locations.sort((a, b) => b.count - a.count );

        // const locations = Array.from(locationCounts, ([scene, count]) => ({ 
        //     scene, 
        //     count, 
        //     percentage: (count / totalCount) * 100 
        // }));
        
        // locations.sort((a, b) => b.percentage - a.percentage);

        const margin = { top: 35, right: 1, bottom: 80, left: 35 };
        const { width, height } = dimensions;

        const svg = d3.select(svgRef.current)
        .attr("width", width)
        .attr("height", height);

        d3.select(svgRef.current).selectAll('*').remove();

        const x = d3.scaleBand()
        // .domain(locations.map(d => d.scene))
        .domain(Array.from(allScenes))
        .range([margin.left, width - margin.right])
        .padding(isMobile ? 0.05 : 0.4);

        const y = d3.scaleLinear()
        .domain([0, 45])
        .nice()
        .range([height - margin.bottom, margin.top]);       
        
        svg.append("g")
        .attr("class", "svg-grid")
        .attr("transform", `translate(${margin.left}, 0)`)
        .call(
            d3.axisLeft(y)
            .tickSize(-width + margin.left + margin.right)
            .tickFormat("")
            )
        .select(".domain")
        .attr("stroke", "none");

        // const barWidth = x.bandwidth() / datasets.length;

        datasets.forEach(({ country, cn, data, color }, datasetIndex) => {
            if (!data || data.length === 0) return;
            
            const totalCount = data.length;
            const locationCounts = d3.rollup(
                data,
                (v) => v.length,
                (d) => d.scene
            );
        
            const locations = Array.from(locationCounts, ([scene, count]) => ({
                scene,
                count,
                percentage: (count / totalCount) * 100,
                cn
            }));
            
            locations.sort((a, b) => b.count - a.count);

            const Tooltip = d3.select("body")
            .append("div")
            .attr("class", "tooltip")
            .style("opacity", 0);


            const handleMouseOver = (event, d) => {
                Tooltip.style("opacity", 1);
                d3.select(event.currentTarget)
                    .raise()
                    .transition()
                    .duration(200)
                    .attr("stroke", "#fff");

                d3.selectAll("rect")
                    .transition()
                    .duration(0)
                    .attr("opacity", 0.3);

                d3.selectAll(`.bar-${country}`)
                    .transition()
                    .duration(0)
                    .attr("opacity",1);
            };

            const handleMouseMove = (event, d) => {
                Tooltip
                    .html(`
                        <span style="color:var(--${country})"><b>${d.cn}</b></span>
                        <br>
                        <b><span class="tooltip__num">${d.count}</span></b>起事件发生在${d.scene}
                        <br>${d.percentage.toFixed(1)}%`)
                    .style("left", (event.pageX + 10) + "px")
                    .style("top", (event.pageY + 10) + "px");
                    
            };

            const handleMouseOut = (event, d) => {
                Tooltip.style("opacity", 0);
                d3.select(event.currentTarget)
                    .transition()
                    .duration(200)
                    .attr("stroke", color);
                d3.selectAll("rect")
                    .transition()
                    .duration(200)
                    .attr("opacity", 1)
                    .attr("stroke-opacity", 1);
            };
        
            const numberOfDatasets = datasets.filter(d => d.data && d.data.length > 0).length;
            const barWidth = Math.max(1, (x.bandwidth() / Math.max(1, numberOfDatasets)) - 1.5);

            svg.append("g")
            .selectAll(`.bar-${country}`)
            .data(locations)
            .enter()
            .append("rect")
            .attr("class", `bar-${country}`)
            // .attr("x", d => x(d.scene) + datasetIndex * barWidth)
            .attr("x", d => {
                const xPos = x(d.scene) + datasetIndex * barWidth;
                return Math.max(margin.left, xPos);
            })
            // .attr("y", d => y(d.percentage))
            .attr("y", d => {
                // Ensure non-negative height
                const yPos = y(d.percentage);
                return Math.min(yPos, height - margin.bottom);
            })
            // .attr("width", barWidth - 1.5)
            .attr("width", () => {
                return Math.max(1, barWidth);
            })
            // .attr("height", d => height - margin.bottom - y(d.percentage))
            .attr("height", d => {
                // Calculate height, ensuring it's not negative
                const rectHeight = height - margin.bottom - y(d.percentage);
                return Math.max(0, rectHeight);
            })
            .attr("fill", color)
            .attr("fill-opacity", 0.7)
            .attr("stroke", color)
            .attr("stroke-opacity", 1)
            .on("mouseover", handleMouseOver)
            .on("mousemove", handleMouseMove)
            .on("mouseout", handleMouseOut);

            svg.append("g")
            // .attr("class", "x-axis")
            .style("font-size", isMobile ? "10px" : "12px")
            .style("letter-spacing", "1px")
            .attr("transform", `translate(-3,${height - margin.bottom})`)
            .call(d3.axisBottom(x)
                .tickSize(0)
                .tickPadding(10))
            .selectAll("text")
            .attr("transform", "rotate(-35)")
            .style("text-anchor", "end");

            svg.append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${margin.left},0)`)
            .call(d3.axisLeft(y)
                .tickSize(0)
                .tickPadding(8)
                .tickFormat(d => d !== 0 ? `${d}%` : ""))
            .select(".domain")
            .attr("opacity", 0);

            
        
        });

        svg.append('text')
            .attr("class","axes")
            .attr("x", 4)
            .attr("y", 13)
            .text("袭击频率百分比");

        

    }, [dimensions, chinaData, usaData, japanData, taiwanData, datasets]);


  return (
    <div>
        <div className="center-container">
            <div className="article-body">
                <h2>无差别袭击事件最常发生在街道与学校</h2>
            </div>
            <div className="spacerdiv" />
        </div>
        <div ref={containerRef}  style={{ height: "600px", maxWidth:"1250px", margin:"0 auto" }} className="container-ref">
            <svg ref={svgRef}></svg>
        </div>

        <div className="center-container">
           
            <div className="article-body">
            <div className="spacerdiv" />
            <p>在中国，无一例外的，街市和学校都属无差别袭击案件频发的地区。最严重的一次，是2001年江西宜春的一个小学发生的<a href="https://news.sina.com.cn/c/204844.html" target="_blank">爆炸案</a>，共造成42人死亡，27人受伤。最近的一次，是2024年11月无锡工艺技术学院持刀伤人事件，死伤人数也有25人。</p>
            <p>令人心痛的是，年少的孩子常常成为无差别袭击事件的受害者。马子琪和赵云亭在《社会排斥与行为极化：个体报复社会事件的生成机理及防控》一文解释，不同于普通个体犯罪的隐匿性，制造轰动效应是个体报复社会的目的之一，犯罪者追求的是社会影响或社会危害的最大化，为此，他们一般会选择社会关注的弱者下手。</p>
            <p>经历这样的暴力事件对于孩子的未来会有更深远的影响。美国关于校园枪击案的<a href="https://siepr.stanford.edu/publications/health/surviving-school-shooting-impacts-mental-health-education-and-earnings-american" target="_blank">研究</a>表明，学生在校园经历枪击案比在其它环境下能感受到更强的痛苦。因为他们通常与受害的师生有更直接的联系，而且也失去了对学校的信任。经历校园枪击案的学生此后也更容易患抑郁症等情绪疾病，学业成绩和出勤率也有明显影响。</p>
            <p>很难想象，人在何种情形下，才会选择向社会关注的“弱者”孩子行凶。美国洛克菲勒（Rockefeller）枪支暴力研究社的行政主任贾克琳·希尔德克劳特 （Jaclyn Schildkraut）曾在接受媒体采访时说，“如果要试图理解枪支暴力的根本原因，不管受害方是谁，我们需要首先从理解人们为什么会拿起枪支来伤害别人开始。”</p>


            </div>
        </div>
    </div>
    
   
  )
}


export default LocationFrequency;